import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUserSession } from '../utils/userAuth';

interface MarketingAuthContextType {
    marketingData: any | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    logout: () => void;
    user: any | null;
}

const MarketingAuthContext = createContext<MarketingAuthContextType | undefined>(undefined);

export const useMarketingAuth = () => {
    const context = useContext(MarketingAuthContext);
    if (!context) {
        throw new Error('useMarketingAuth must be used within a MarketingAuthProvider');
    }
    return context;
};

interface MarketingAuthProviderProps {
    children: ReactNode;
}

export const MarketingAuthProvider: React.FC<MarketingAuthProviderProps> = ({ children }) => {
    const [marketingData, setMarketingData] = useState<any | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const handleForcedLogout = useCallback(() => {
        clearUserSession();
        setToken(null);
        setMarketingData(null);
        navigate('/user/login');
    }, [navigate]);

    // Initialize auth state from localStorage
    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem('userToken');
            const storedMarketing = localStorage.getItem('marketingInfo');

            if (storedToken) {
                setToken(storedToken);
            }

            if (storedMarketing) {
                try {
                    const marketingDataParsed = JSON.parse(storedMarketing);
                    setMarketingData(marketingDataParsed);
                } catch (error) {
                    clearUserSession();
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, []);

    const logout = useCallback(() => {
        handleForcedLogout();
    }, [handleForcedLogout]);

    const isAuthenticated = !!token;

    return (
        <MarketingAuthContext.Provider
            value={{
                marketingData,
                token,
                isAuthenticated,
                isLoading,
                logout,
                user: null,
            }}
        >
            {children}
        </MarketingAuthContext.Provider>
    );
};
